<template>
  <div class="secret-game">
    <header class="toolbar">
      <div class="row">
        <label>🎯 Dificultad:</label>
        <select v-model="selectedDifficulty">
          <option value="easy">Fácil (16×16)</option>
          <option value="normal">Normal (20×20)</option>
          <option value="hard">Difícil (26×26)</option>
        </select>

        <label>🎨 Tema:</label>
        <select v-model="settings.theme">
          <option value="classic">Clásico</option>
          <option value="neon">Neón</option>
        </select>

        <label class="checkbox">
          <input type="checkbox" v-model="settings.showGrid" />
          Rejilla
        </label>

        <label class="checkbox">
          <input type="checkbox" v-model="settings.wrapWalls" />
          Pasar paredes
        </label>
      </div>

      <div class="row">
        <button @click="startGame" v-if="state !== 'playing'">
          ▶️ Empezar
        </button>
        <button @click="pauseGame" v-if="state === 'playing'">⏸️ Pausa</button>
        <button @click="resumeGame" v-if="state === 'paused'">
          ▶️ Reanudar
        </button>
        <button @click="resetGame">🔄 Reiniciar</button>
      </div>
    </header>

    <main class="content">
      <GameBoard
        :board-size="boardSize"
        :snake-body="snakeBody"
        :food-position="food"
        :obstacles="obstacles"
        :power-up="powerUp"
        :theme="settings.theme"
        :show-grid="settings.showGrid"
      />

      <aside class="side">
        <HighScores :scores="highScores" />
        <div class="stats">
          <p><strong>Puntos:</strong> {{ score }}</p>
          <p><strong>Mejor:</strong> {{ bestScore }}</p>
          <p><strong>Estado:</strong> {{ stateLabel }}</p>
        </div>
      </aside>
    </main>
  </div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { ref, reactive, computed, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/stores/authStore";
import api from "@/services/api";
import GameBoard from "@/components/GameBoard.vue";
import HighScores from "@/components/HighScores.vue";

// -------------------- Configuración --------------------
const DIFFICULTIES = {
  easy: { label: "Fácil", board: 16, tick: 180, obstacles: 8 },
  normal: { label: "Normal", board: 20, tick: 130, obstacles: 14 },
  hard: { label: "Difícil", board: 26, tick: 95, obstacles: 22 },
};

// Obtener nombre del usuario autenticado o "You" si no hay
const auth = useAuthStore();
const playerName = computed(() => auth?.user?.name?.trim() || "You");

// Mínimo y máximo del tick para que sea jugable
const MIN_TICK = 80;
const MAX_TICK = 280;

// -------------------- Estado principal --------------------
const state = ref("menu"); // menu | playing | paused | gameover
const selectedDifficulty = ref("normal");
const settings = reactive({
  theme: "classic",
  showGrid: true,
  wrapWalls: false,
});

const boardSize = computed(() => DIFFICULTIES[selectedDifficulty.value].board);
const tickMs = computed(() =>
  Math.min(
    Math.max(DIFFICULTIES[selectedDifficulty.value].tick, MIN_TICK),
    MAX_TICK
  )
);
const obstacleCount = computed(
  () => DIFFICULTIES[selectedDifficulty.value].obstacles
);

const snakeBody = ref([]);
const direction = ref({ x: 1, y: 0 });
const nextDirection = ref({ x: 1, y: 0 });
const obstacles = ref([]);
const food = ref(null);
const powerUp = ref(null); // { x, y, type }
const score = ref(0);
const bestScore = ref(0);
let timer = null;

const stateLabel = computed(
  () =>
    ({
      menu: "Menú",
      playing: "Jugando",
      paused: "Pausado",
      gameover: "Fin",
    }[state.value] || state.value)
);

// -------------------- Utilidades --------------------
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const wrap = (n) => (n + boardSize.value) % boardSize.value;

const equalPos = (a, b) => a && b && a.x === b.x && a.y === b.y;

function isOccupied(pos, { includeSnakeTail = true } = {}) {
  // Obstáculos
  if (obstacles.value.some((o) => equalPos(o, pos))) return true;
  // Serpiente
  const len = includeSnakeTail
    ? snakeBody.value.length
    : Math.max(snakeBody.value.length - 1, 0);
  for (let i = 0; i < len; i++) {
    if (equalPos(snakeBody.value[i], pos)) return true;
  }
  return false;
}

function rndInt(max) {
  return Math.floor(Math.random() * max);
}

function randomFreeCell() {
  let tries = 0;
  while (tries++ < 5000) {
    const pos = { x: rndInt(boardSize.value), y: rndInt(boardSize.value) };
    if (!isOccupied(pos)) return pos;
  }
  // Fallback raro si está casi lleno
  return { x: 0, y: 0 };
}

function generateObstacles(n) {
  obstacles.value = [];
  const center = Math.floor(boardSize.value / 2);
  const forbidden = new Set([
    `${center},${center}`,
    `${center + 1},${center}`,
    `${center},${center + 1}`,
  ]);
  while (obstacles.value.length < n) {
    const p = { x: rndInt(boardSize.value), y: rndInt(boardSize.value) };
    if (forbidden.has(`${p.x},${p.y}`)) continue;
    if (
      !isOccupied(p, { includeSnakeTail: false }) &&
      !obstacles.value.some((o) => equalPos(o, p))
    ) {
      obstacles.value.push(p);
    }
  }
}

function placeFood() {
  food.value = randomFreeCell();
}

// -------------------- Ciclo de juego --------------------
function startLoop() {
  stopLoop();
  timer = setInterval(step, tickMs.value);
}

function stopLoop() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

function step() {
  // Actualizar dirección (sin permitir reversa inmediata)
  if (
    (nextDirection.value.x !== 0 && direction.value.x === 0) ||
    (nextDirection.value.y !== 0 && direction.value.y === 0)
  ) {
    direction.value = { ...nextDirection.value };
  }

  // Nueva cabeza
  let nx = snakeBody.value[0].x + direction.value.x;
  let ny = snakeBody.value[0].y + direction.value.y;

  if (settings.wrapWalls) {
    nx = wrap(nx);
    ny = wrap(ny);
  }

  const newHead = { x: nx, y: ny };

  // Colisiones bordes
  if (!settings.wrapWalls) {
    if (nx < 0 || ny < 0 || nx >= boardSize.value || ny >= boardSize.value) {
      return endGame();
    }
  }
  // Colisión obstáculos
  if (obstacles.value.some((o) => equalPos(o, newHead))) return endGame();
  // Colisión cuerpo (ignora la cola que se mueve)
  for (let i = 0; i < snakeBody.value.length - 1; i++) {
    if (equalPos(snakeBody.value[i], newHead)) return endGame();
  }

  // Avanzar
  snakeBody.value.unshift(newHead);

  // Comer comida
  if (food.value && equalPos(food.value, newHead)) {
    score.value += 10;
    placeFood();
    trySaveBest();
    // chance de power-up
    maybeSpawnPowerUp();
  } else {
    // sin comida: quitar cola
    snakeBody.value.pop();
  }

  // Power up recogido
  if (powerUp.value && equalPos(powerUp.value, newHead)) {
    applyPowerUp(powerUp.value.type || "score");
    powerUp.value = null;
  }
}

function endGame() {
  state.value = "gameover";
  stopLoop();
  trySaveScoreEntry();
}

// -------------------- Power ups sencillos --------------------
function maybeSpawnPowerUp() {
  // 15% probabilidad
  if (Math.random() < 0.15) {
    let p = randomFreeCell();
    powerUp.value = { ...p, type: Math.random() < 0.5 ? "score" : "slow" };
    // Desaparece en 8 s si no se recoge
    setTimeout(() => {
      if (powerUp.value && equalPos(powerUp.value, p)) powerUp.value = null;
    }, 8000);
  }
}

function applyPowerUp(type) {
  if (type === "score") {
    score.value += 20;
    trySaveBest();
  } else if (type === "slow") {
    // Ralentiza temporalmente: aumenta el tick en 40 ms durante 6 s
    const old = tickMs.value;
    const newTick = clamp(old + 40, MIN_TICK, MAX_TICK);
    // Simular ralentización cambiando temporalmente el intervalo
    stopLoop();
    timer = setInterval(step, newTick);
    setTimeout(() => {
      if (state.value === "playing") {
        stopLoop();
        timer = setInterval(step, tickMs.value);
      }
    }, 6000);
  }
}

// -------------------- Controles --------------------
function onKeydown(e) {
  if (
    ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(e.key)
  ) {
    e.preventDefault();
  }
  switch (e.key) {
    case "ArrowUp":
    case "w":
    case "W":
      if (direction.value.y === 0) nextDirection.value = { x: 0, y: -1 };
      break;
    case "ArrowDown":
    case "s":
    case "S":
      if (direction.value.y === 0) nextDirection.value = { x: 0, y: 1 };
      break;
    case "ArrowLeft":
    case "a":
    case "A":
      if (direction.value.x === 0) nextDirection.value = { x: -1, y: 0 };
      break;
    case "ArrowRight":
    case "d":
    case "D":
      if (direction.value.x === 0) nextDirection.value = { x: 1, y: 0 };
      break;
    case " ":
      if (state.value === "playing") pauseGame();
      else if (state.value === "paused") resumeGame();
      else if (state.value === "menu" || state.value === "gameover")
        startGame();
      break;
  }
}

// Controles táctiles básicos (swipe)
let touchStart = null;
function onTouchStart(e) {
  touchStart = e.touches[0];
}
function onTouchMove(e) {
  if (!touchStart) return;
  const dx = e.touches[0].clientX - touchStart.clientX;
  const dy = e.touches[0].clientY - touchStart.clientY;
  const ax = Math.abs(dx),
    ay = Math.abs(dy);
  if (ax > 24 || ay > 24) {
    if (ax > ay) {
      // horizontal
      if (dx > 0 && direction.value.x === 0)
        nextDirection.value = { x: 1, y: 0 };
      else if (dx < 0 && direction.value.x === 0)
        nextDirection.value = { x: -1, y: 0 };
    } else {
      // vertical
      if (dy > 0 && direction.value.y === 0)
        nextDirection.value = { x: 0, y: 1 };
      else if (dy < 0 && direction.value.y === 0)
        nextDirection.value = { x: 0, y: -1 };
    }
    touchStart = null;
  }
}

// -------------------- Flujo de juego --------------------
function startGame() {
  resetGame();
  state.value = "playing";
  startLoop();
}
function pauseGame() {
  state.value = "paused";
  stopLoop();
}
function resumeGame() {
  state.value = "playing";
  startLoop();
}

function resetGame() {
  // Recalcula todo por si cambió boardSize
  const c = Math.floor(boardSize.value / 2);
  snakeBody.value = [{ x: c, y: c }];
  direction.value = { x: 1, y: 0 };
  nextDirection.value = { x: 1, y: 0 };
  obstacles.value = [];
  powerUp.value = null;
  score.value = 0;
  generateObstacles(obstacleCount.value);
  placeFood();
}

// -------------------- Highscores --------------------
const STORAGE_KEY = "vue-snake-highscores";
const highScores = ref([]);
async function fetchGlobalHighScores() {
  try {
    // antes: localStorage
    loadHighScores();
    highScores.value = await api.snakeGetHighScores({
      scope: "global",
      limit: 50,
    });
    const best = Math.max(0, ...highScores.value.map((s) => s.points || 0));
    bestScore.value = Number.isFinite(best) ? best : 0;
  } catch (e) {
    console.warn("Fallo cargando highscores globales, uso local", e);
    loadHighScores();
  }
}

async function trySaveScoreEntry() {
  if (score.value <= 0) return;
  try {
    await api.snakePostHighScore(score.value);
    await fetchGlobalHighScores();
  } catch (e) {
    console.warn("No se pudo enviar score global, guardo local", e);
    const entry = {
      id: Date.now(),
      name: playerName.value,
      points: score.value,
    };
    highScores.value.push(entry);
    highScores.value.sort((a, b) => b.points - a.points);
    highScores.value = highScores.value.slice(0, 10);
    saveHighScores();
  }
}
function loadHighScores() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    highScores.value = raw ? JSON.parse(raw) : [];
  } catch {
    highScores.value = [];
  }
}
function saveHighScores() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(highScores.value));
  } catch (err) {
    // Non-fatal (modo privado / cuota superada / storage deshabilitado)
    // eslint-disable-next-line no-console
    console.warn("[HighScores] No se pudieron guardar los datos:", err);
  }
}
function trySaveBest() {
  if (score.value > bestScore.value) bestScore.value = score.value;
}
/*function trySaveScoreEntry() {
  if (score.value <= 0) return;
  const entry = { id: Date.now(), name: playerName.value, points: score.value };
  highScores.value.push(entry);
  highScores.value.sort((a, b) => b.points - a.points);
  highScores.value = highScores.value.slice(0, 10);
  saveHighScores();
}*/

// -------------------- Ciclo de vida --------------------
onMounted(() => {
  fetchGlobalHighScores(); // ya hace fallback a local y calcula bestScore
  window.addEventListener("keydown", onKeydown);
  window.addEventListener("touchstart", onTouchStart, { passive: true });
  window.addEventListener("touchmove", onTouchMove, { passive: true });
});
onUnmounted(() => {
  stopLoop();
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("touchstart", onTouchStart);
  window.removeEventListener("touchmove", onTouchMove);
});
</script>

<style scoped>
.secret-game {
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 12px;
  padding: 12px;
}
.toolbar {
  display: grid;
  gap: 8px;
}
.toolbar .row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.toolbar label {
  font-weight: 600;
}
.toolbar select,
.toolbar button,
.toolbar input[type="checkbox"] {
  font-size: 14px;
  padding: 6px 10px;
}
.checkbox {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.content {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 16px;
  align-items: start;
}
@media (max-width: 900px) {
  .content {
    grid-template-columns: 1fr;
  }
}

.side {
  display: grid;
  gap: 12px;
}
.stats {
  padding: 12px;
  background: #f4f4f4;
  border-radius: 8px;
}
</style>
